import api from '../api';
import { nextDelay } from './intervals';

/*
 * The chat's pushed transport (phase D2; docs/FIELD_AI_VISION_SPEC.md section
 * 33; the server end is routes/chat_ws.py and infra/lib/chat-ws-stack.ts).
 *
 * REST writes, the socket pokes. One WebSocket for the whole app, opened while
 * anything listens: the chat list, an open conversation, the unread badge.
 * The server sends the ids of what changed -- {type, channel_id, message_id?}
 * -- never the content; each listener then asks its own live channel to read
 * now (utils/live/liveChannel.js `poke`), over REST, where who may read what
 * is checked as always.
 *
 *   const off = chatPush().subscribe({ channelId, onPoke });   // null channelId: every poke
 *   chatPush().onState((state) => ...);                         // 'open' while pokes flow
 *
 * While the socket is open the listeners slow their polling to a five-minute
 * safety net (SAFETY_NET_MS); while it is not, they poll as before. So a
 * socket that fails costs nothing but its retries.
 *
 *   - A ticket first: POST /chat/ws-ticket gives a 60-second, single-use one,
 *     so the session token never goes into a URL. A fresh one for every
 *     connection.
 *   - The socket is pinged every 5 minutes (API Gateway closes an idle one at
 *     10, and every one at 2 hours); a closed one is opened again with the
 *     backoff of intervals.js, a new ticket each time; offline, it waits for
 *     the connection to come back.
 *   - 503 push_not_configured (staging, the desktop, before the stack is
 *     deployed): state 'off', asked again only after OFF_MS. 401 and 403
 *     (signed out, no chat, viewing as a role): 'off' until the app reloads.
 *     The request neither signs anyone out nor announces read-only mode:
 *     it is the app's own business, in the background.
 *   - The last listener gone, the socket closes.
 *
 * The app's one instance is chatPush(). Under Vitest it is inert (no ticket,
 * no socket), so a page's tests poll exactly as before; tests of this module
 * make their own with createChatPush, and a page's test can hand a fake to
 * the hook (hooks/useChatPush.js, ChatPushContext).
 */
export const SAFETY_NET_MS = 5 * 60 * 1000;
export const PING_MS = 5 * 60 * 1000;
export const OFF_MS = 30 * 60 * 1000;
export const RECONNECT = Object.freeze({ base: 2000, max: 5 * 60 * 1000 });
export const POKE_TYPES = Object.freeze(['message', 'edit', 'retract', 'reaction', 'read', 'members', 'rename']);
const OPEN = 1;

function askTicket() {
  return api.post('/chat/ws-ticket', null, { keepSession: true, quietReadOnly: true }).then((r) => r.data);
}

/** A poke the server may send, checked; null for anything else. */
export function readPoke(data) {
  let poke;
  try {
    poke = typeof data === 'string' ? JSON.parse(data) : null;
  } catch {
    return null;
  }
  if (!poke || !POKE_TYPES.includes(poke.type) || typeof poke.channel_id !== 'string') return null;
  const out = { type: poke.type, channel_id: poke.channel_id };
  if (Number.isInteger(poke.message_id) && poke.message_id > 0) out.message_id = poke.message_id;
  return out;
}

export function createChatPush({
  requestTicket = askTicket,
  WebSocketImpl = typeof WebSocket !== 'undefined' ? WebSocket : null,
  timers = {
    setTimeout: (...a) => setTimeout(...a), clearTimeout: (...a) => clearTimeout(...a),
    setInterval: (...a) => setInterval(...a), clearInterval: (...a) => clearInterval(...a),
  },
  random,
  isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false,
  win = typeof window !== 'undefined' ? window : null,
} = {}) {
  const listeners = new Set();
  const watchers = new Set();
  let state = 'idle';            // idle | connecting | open | waiting | off
  let socket = null;
  let failures = 0;
  let retry = null;
  let ping = null;
  let generation = 0;            // a later connect makes an earlier one's callbacks moot

  const setState = (next) => {
    if (state === next) return;
    state = next;
    watchers.forEach((fn) => fn(next));
  };
  const clearRetry = () => {
    if (retry !== null) timers.clearTimeout(retry);
    retry = null;
  };
  const clearPing = () => {
    if (ping !== null) timers.clearInterval(ping);
    ping = null;
  };
  const onOnline = () => { if (listeners.size && state === 'waiting') connect(); };
  const later = (delay, as = 'waiting') => {
    clearRetry();
    setState(as);
    retry = timers.setTimeout(() => { retry = null; connect(); }, delay);
  };
  const failed = () => {
    failures += 1;
    later(nextDelay({ failures, ...RECONNECT, random }));
  };

  function close() {
    generation += 1;
    clearRetry();
    clearPing();
    win?.removeEventListener?.('online', onOnline);
    const open = socket;
    socket = null;
    if (open) {
      try { open.close(1000); } catch { /* already closing */ }
    }
  }

  async function connect() {
    if (!listeners.size || !WebSocketImpl) {
      setState(listeners.size ? 'off' : 'idle');
      return;
    }
    if (!isOnline()) {
      setState('waiting');
      win?.addEventListener?.('online', onOnline, { once: true });
      return;
    }
    generation += 1;
    const mine = generation;
    setState('connecting');
    let answer;
    try {
      answer = await requestTicket();
    } catch (error) {
      if (mine !== generation) return;
      const status = error?.response?.status;
      if (status === 503 && error?.response?.data?.code === 'push_not_configured') {
        later(OFF_MS, 'off');
      } else if (status === 401 || status === 403) {
        setState('off');
      } else {
        failed();
      }
      return;
    }
    if (mine !== generation) return;
    if (!answer?.url || !answer?.ticket) {
      failed();
      return;
    }
    let ws;
    try {
      ws = new WebSocketImpl(`${answer.url}?ticket=${encodeURIComponent(answer.ticket)}`);
    } catch {
      failed();
      return;
    }
    socket = ws;
    ws.onopen = () => {
      if (mine !== generation) return;
      failures = 0;
      setState('open');
      clearPing();
      ping = timers.setInterval(() => {
        if (socket === ws && ws.readyState === OPEN) ws.send('{"action":"ping"}');
      }, PING_MS);
    };
    ws.onmessage = (event) => {
      if (mine !== generation) return;
      const poke = readPoke(event?.data);
      if (!poke) return;
      listeners.forEach((l) => {
        if (!l.channelId || l.channelId === poke.channel_id) l.onPoke(poke);
      });
    };
    ws.onclose = () => {
      if (mine !== generation) return;
      socket = null;
      clearPing();
      if (!listeners.size) {
        setState('idle');
        return;
      }
      failed();
    };
    ws.onerror = () => { /* a close follows */ };
  }

  return {
    subscribe({ channelId = null, onPoke }) {
      const listener = { channelId, onPoke };
      listeners.add(listener);
      if (state === 'idle') connect();
      return () => {
        listeners.delete(listener);
        if (!listeners.size) {
          close();
          failures = 0;
          setState('idle');
        }
      };
    },
    onState(fn) {
      watchers.add(fn);
      return () => watchers.delete(fn);
    },
    get state() { return state; },
  };
}

// Inert under Vitest: no ticket, no socket, every listener polls.
const inert = Object.freeze({
  subscribe: () => () => {},
  onState: () => () => {},
  state: 'off',
});

let shared = null;

/** The app's one push transport. */
export function chatPush() {
  if (!shared) shared = import.meta.env?.MODE === 'test' ? inert : createChatPush();
  return shared;
}
